import { BaseAPI } from "./BaseAPI";
import generateNewAdvertising from "../utils/data/advertisings";
import { generateNewOffer } from "../utils/data/Offers";

import type { APIConf } from "./BaseAPI";
import type {
  CreateAdvertisingResponse,
  CreateAdvertisingTranslationResponse,
  CreateOfferPlanParams,
  CreateOfferTranslationResponse,
  DeletePictureParams,
  FreecopyCreate,
  FreecopyResendEmail,
  FreecopyUpdate,
  GetFreecopyBatchResponse,
  GetFreecopyListResponse,
  GetFreecopyResponse,
  GetFreecopyUniqueCodeListResponse,
  GetGiftListParams,
  GetGiftListResponse,
  GetMediaUrlParams,
  GetMediaUrlResponse,
  GetOfferListResponse,
  GetOfferPlanListResponse,
  GetOfferResponse,
  OfferPlatform,
  PostFreecopyResponse,
  UpdateOfferParams,
  UpdateOfferPlanParams,
  UpdateOfferPlanResponse,
  UploadAdvertisingFileResponse,
  UploadPictureParams,
  UploadPictureResponse,
} from "./OfferAPI.types";
import type { IAdvertisingSingle, IGetAdvertisingResponse } from "../types";

class OfferAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "offer";
    this.version = "v6";
  }

  async getGiftList({
    projectSlug,
  }: GetGiftListParams): Promise<GetGiftListResponse> {
    return this.axios
      .get<GetGiftListResponse>(`${this.apiBase}/offer/v4/admin/gifts`, {
        headers: { "xx-ok-project": projectSlug },
        params: {
          page: 1,
          per_page: 1000000,
        },
      })
      .then((response) => response.data);
  }

  async deleteOfferPlan({
    planId,
    projectSlug,
  }: {
    planId: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/offer_plans/${planId}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async createOfferPlan({
    projectSlug,
    plan,
  }: CreateOfferPlanParams): Promise<UpdateOfferPlanResponse> {
    return this.axios
      .post<UpdateOfferPlanResponse>(
        `${this.routeBase}/admin/offer_plans/`,
        plan,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async updateOfferPlan({
    projectSlug,
    plan,
  }: UpdateOfferPlanParams): Promise<UpdateOfferPlanResponse> {
    return this.axios
      .put<UpdateOfferPlanResponse>(
        `${this.routeBase}/admin/offer_plans/${plan.uuid}`,
        plan,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getOffers({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<GetOfferListResponse> {
    return this.axios
      .get<GetOfferListResponse>(`${this.routeBase}/admin/offers`, {
        headers: { "xx-ok-project": projectSlug },
        params: {
          page: 1,
          per_page: 100000,
          current_sfp_only: true, // Only get offers from that project
        },
      })
      .then((response) => response.data);
  }

  async getOffer({
    offerId,
    projectSlug,
  }: {
    offerId: string;
    projectSlug: string;
  }): Promise<GetOfferResponse> {
    return this.axios
      .get<GetOfferResponse>(`${this.routeBase}/admin/offers/${offerId}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async updateOffer({
    offer,
    projectSlug,
  }: UpdateOfferParams): Promise<unknown> {
    return this.axios
      .put(`${this.routeBase}/admin/offers/${offer.uuid}`, offer, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async createOffer({
    t,
    languageUUID,
    projectSlug,
  }: {
    t: (key: string) => string;
    languageUUID: string;
    projectSlug: string;
  }): Promise<GetOfferResponse> {
    const offer = generateNewOffer({
      t,
      languageUUID,
    });

    return this.axios
      .post<GetOfferResponse>(`${this.routeBase}/admin/offers`, offer, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async deleteOffer({
    uuid,
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/offers/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getOfferPlans({
    projectSlug,
    offerPlatform,
  }: {
    projectSlug: string;
    offerPlatform?: OfferPlatform;
  }): Promise<GetOfferPlanListResponse> {
    return this.axios
      .get<GetOfferPlanListResponse>(`${this.routeBase}/admin/offer_plans`, {
        headers: { "xx-ok-project": projectSlug },
        params: {
          enabled: true,
          platform: offerPlatform,
        },
      })
      .then((response) => response.data);
  }

  async getFreecopies({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<GetFreecopyListResponse> {
    return this.axios
      .get<GetFreecopyListResponse>(`${this.routeBase}/admin/free_copies`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getFreecopy({
    uuid,
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<GetFreecopyResponse> {
    if (!uuid) {
      return Promise.reject(new Error("No uuid provided"));
    }
    return this.axios
      .get<GetFreecopyResponse>(`${this.routeBase}/admin/free_copies/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async createFreecopy({
    data,
    projectSlug,
  }: {
    data: FreecopyCreate;
    projectSlug: string;
  }): Promise<PostFreecopyResponse> {
    return this.axios
      .post<PostFreecopyResponse>(`${this.routeBase}/admin/free_copies`, data, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async updateFreecopy({
    uuid,
    data,
    projectSlug,
  }: {
    uuid: string;
    data: FreecopyUpdate | FreecopyResendEmail;
    projectSlug: string;
  }): Promise<void> {
    return this.axios.put(`${this.routeBase}/admin/free_copies/${uuid}`, data, {
      headers: { "xx-ok-project": projectSlug },
    });
  }

  async deleteFreecopy({
    uuid,
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/free_copies/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getFreecopiesBatch({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<GetFreecopyBatchResponse> {
    return this.axios
      .get<GetFreecopyBatchResponse>(
        `${this.routeBase}/admin/free_copies_batch`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getFreecopiesUniqueCode({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<GetFreecopyUniqueCodeListResponse> {
    return this.axios
      .post<GetFreecopyUniqueCodeListResponse>(
        `${this.routeBase}/admin/free_copies_unique_code`,
        {
          page: 1,
          per_page: 100000,
        },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getFreecopyUniqueCode({
    uuid,
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .get(`${this.routeBase}/admin/free_copy_unique_code/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async checkFreecopyUniqueCode({
    code,
    projectSlug,
  }: {
    code: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .get(`${this.routeBase}/admin/free_copy_unique_code/validate/${code}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async createFreecopyUniqueCode({
    data,
    projectSlug,
  }: {
    data: unknown;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .post(`${this.routeBase}/admin/free_copy_unique_code`, data, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async updateFreecopyUniqueCode({
    uuid,
    data,
    projectSlug,
  }: {
    uuid: string;
    data: unknown;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .put(`${this.routeBase}/admin/free_copy_unique_code/${uuid}`, data, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async exportFreecopyUniqueCode({
    uuid,
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<string> {
    return this.axios
      .get<string>(
        `${this.routeBase}/admin/free_copy_unique_code/${uuid}/export`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getSingleAdvertising({
    uuid = "",
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<IAdvertisingSingle> {
    return this.axios
      .get<IAdvertisingSingle>(`${this.routeBase}/admin/advertisings/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async getAdvertisingList({
    projectSlug,
  }: {
    projectSlug: string;
  }): Promise<IGetAdvertisingResponse> {
    return this.axios
      .get<IGetAdvertisingResponse>(`${this.routeBase}/admin/advertisings/`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async deleteAdvertising({
    uuid,
    projectSlug,
  }: {
    uuid: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/advertisings/${uuid}`, {
        headers: { "xx-ok-project": projectSlug },
      })
      .then((response) => response.data);
  }

  async updateAdvertising({
    item,
    projectSlug,
  }: {
    item: IAdvertisingSingle;
    projectSlug: string;
  }): Promise<CreateAdvertisingResponse> {
    return this.axios
      .put<CreateAdvertisingResponse>(
        `${this.routeBase}/admin/advertisings/${item.uuid}`,
        { ...item },
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async uploadAdvertisingFile({
    uuid,
    file,
    languageUuid,
    onUploadProgress,
    projectSlug,
    signal,
  }: {
    uuid: string;
    file: File;
    languageUuid: string;
    onUploadProgress: (progressEvent: ProgressEvent) => void;
    projectSlug: string;
    signal?: AbortSignal;
  }): Promise<UploadAdvertisingFileResponse> {
    const data = new FormData();
    data.append("video", file);
    data.append("language_uuid", languageUuid);

    return this.axios
      .post<UploadAdvertisingFileResponse>(
        `${this.routeBase}/admin/advertisings/${uuid}/upload`,
        data,
        {
          headers: {
            "xx-ok-project": projectSlug,
          },
          onUploadProgress,
          signal,
        },
      )
      .then((response) => response.data);
  }

  async deleteAdvertisingFile({
    uuid,
    translationUuid,
    projectSlug,
  }: {
    uuid: string;
    translationUuid: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(
        `${this.routeBase}/admin/advertisings/${uuid}/translation/${translationUuid}/video`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async deleteAdvertisingTranslation({
    uuid,
    translationUuid,
    projectSlug,
  }: {
    uuid: string;
    translationUuid: string;
    projectSlug: string;
  }): Promise<unknown> {
    return this.axios
      .delete(
        `${this.routeBase}/admin/advertisings/${uuid}/translation/${translationUuid}`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async createAdvertising({
    type,
    languageId,
    projectSlug,
  }: {
    type: string;
    languageId: string;
    projectSlug: string;
  }): Promise<CreateAdvertisingResponse> {
    if (!type || !languageId)
      return Promise.reject(
        new Error("No campaign type or languageId provided"),
      );

    const newAdvertising = generateNewAdvertising({ type, languageId });

    return this.axios
      .post<CreateAdvertisingResponse>(
        `${this.routeBase}/admin/advertisings`,
        newAdvertising,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async createAdvertisingTranslation({
    uuid,
    languageUuid,
    projectSlug,
  }: {
    uuid: string;
    languageUuid: string;
    projectSlug: string;
  }): Promise<CreateAdvertisingTranslationResponse> {
    return this.axios
      .post<CreateAdvertisingTranslationResponse>(
        `${this.routeBase}/admin/advertisings/${uuid}/translation`,
        { language_uuid: languageUuid },
        { headers: { "xx-ok-project": projectSlug } },
      )
      .then((response) => response.data);
  }

  async getMediaUrl({
    mediaUuid,
    language,
  }: GetMediaUrlParams): Promise<GetMediaUrlResponse> {
    return this.axios
      .get<GetMediaUrlResponse>(
        `${this.routeBase}/admin/media/${mediaUuid}/url`,
        {
          params: {
            language,
          },
        },
      )
      .then((response) => response.data);
  }

  async createTranslation({
    offerId,
    languageUuid,
    projectSlug,
  }: {
    offerId: string;
    languageUuid: string;
    projectSlug: string;
  }): Promise<CreateOfferTranslationResponse> {
    return this.axios
      .post<CreateOfferTranslationResponse>(
        `${this.routeBase}/admin/offers/${offerId}/translation`,
        { language_uuid: languageUuid },
        { headers: { "xx-ok-project": projectSlug } },
      )
      .then((response) => response.data);
  }

  async uploadPicture({
    offerId,
    pictureFile,
    pictureFormat,
    translationId,
    projectSlug,
  }: UploadPictureParams): Promise<UploadPictureResponse> {
    const formData = new FormData();
    formData.append("picture", pictureFile);

    return this.axios
      .post<UploadPictureResponse>(
        `${this.routeBase}/admin/offers/${offerId}/picture`,
        formData,
        {
          params: {
            translation_uuid: translationId,
            picture_field: pictureFormat,
          },
          headers: {
            "Content-Type": "multipart/form-data",
            "xx-ok-project": projectSlug,
          },
        },
      )
      .then((response) => response.data);
  }

  async deletePicture({
    offerId,
    translationId,
    projectSlug,
  }: DeletePictureParams): Promise<void> {
    await this.axios.delete(
      `${this.routeBase}/admin/offers/${offerId}/picture`,
      {
        params: {
          translation_uuid: translationId,
        },
        headers: {
          "xx-ok-project": projectSlug,
        },
      },
    );
  }
}

const OfferAPI = new OfferAPIClass({
  token: localStorage.token,
});

export default OfferAPI;
